import {StyledPostMeta, ClapTip} from "./blog-meta.style";
import React, {useEffect, useState, useRef} from 'react';
import {CounterFallbacks} from "../../interact";
import {FaHandsClapping, FaEye, FaAlignJustify, FaComment} from 'react-icons/fa6';
import {incrementClap, fetchCounterData} from "../../interact";
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {getClaps, setCookie} from "../use-cookie";
import PostAudio from "../audio-div/audio-div";
import Tag from "../tag/tag";


interface MetaProps {
    max_n_tags?: number;
    slug: string;
    tags?: Array<string>;
    date?: string;
    can_interact?: boolean;
    hasAudio?: boolean;
    className?: string;
    isVisit?: boolean;
    isNewVisit?: boolean;
    n_fallbacks: CounterFallbacks;
    audioURL?: string | null;
    audio?: number | null;
}

interface TagLineProps {
    tags: string[];
    max_n_tags?: number;
    can_interact?: boolean;
}

export const TagLine: React.FC<TagLineProps> = ({tags, max_n_tags = 3, can_interact = false}) => {
    return (
        <>
            {tags.slice(0, max_n_tags).map((tag, index) => (
                <Tag key={index} text={tag} isLink={can_interact}/>
            ))}
        </>
    );
};


const ScrollToComments: React.FC<{ commentsDisplayed: number, can_interact: boolean }> = ({ commentsDisplayed, can_interact }) => {
  if (!can_interact) {
      return <span><FaComment /> {commentsDisplayed}</span>
  }

  const handleScroll = () => {
    const commentsSection = document.getElementById("comments");
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <span onClick={handleScroll} className='button-like'>
      <FaComment /> {commentsDisplayed}
    </span>
  );
};

const ClappingHands: React.FC<{ clapsDisplayed: number, can_interact: boolean, handleClap: () => void, clapsAdded: number, newHands: () => void}> = ({ clapsDisplayed, can_interact, handleClap, clapsAdded, newHands }) => {
    if (!can_interact) {
        return <span><FaHandsClapping/>{clapsDisplayed}</span>
    }

    return (
        <span className={'confetti-origin button-like'} onClick={can_interact ? handleClap : undefined}>
                        {clapsAdded ? (<ClapTip>+{clapsAdded}</ClapTip>) : null}
            {newHands()}
            <span className='num'>{clapsDisplayed}</span>
        </span>
    )
}

const BlogMeta: React.FunctionComponent<MetaProps> = (
    {
        max_n_tags = 1,
        slug,
        tags = [],
        date = '',
        can_interact = false,
        className = '',
        isVisit = false,
        isNewVisit = false,
        n_fallbacks,
        audioURL = null,
        audio = null,
        ...props
    }) => {
    const {executeRecaptcha} = useGoogleReCaptcha()
    // Manage states of the displayed views and claps
    const [viewsDisplayed, setViewsDisplayed] = useState<number>(isVisit ? n_fallbacks.views + 1 : n_fallbacks.views);
    const [clapsDisplayed, setClapsDisplayed] = useState<number>(n_fallbacks.claps)
    const [commentsDisplayed, setCommentsDisplayed] = useState<number>(n_fallbacks.comments)
    const clapsLoaded = useRef<number>(n_fallbacks.claps)

    // Continuously update the number of claps added
    const [clapsAdded, setClapsAdded] = useState<number>(0)

    // Visuals
    const [enlargeHands, setEnlargeHands] = useState(false);
    // const [confettiVisible, setConfettiVisible] = useState(false);

    useEffect(() => {
        // Check if the cookies are ahead of the fallback
        let loadedClaps = getClaps(slug)
        if (loadedClaps > n_fallbacks.claps) {
            clapsLoaded.current = loadedClaps
        }
        setClapsDisplayed(clapsLoaded.current)

        // Check if the remote is ahead of the cookies
        fetchCounterData(slug, 'views').then((num: number) => {
            num && setViewsDisplayed(num + (isNewVisit ? 1 : 0))
        });
        fetchCounterData(slug, 'comments').then((num: number) => {
            num && setCommentsDisplayed(num)
        });
        fetchCounterData(slug, 'claps').then((num: number) => {
            if (num && num > clapsLoaded.current) {
                clapsLoaded.current = num
                setClapsDisplayed(num)
                setCookie(slug, num)
            }
        });
    }, []);

    const mainTags = <TagLine tags={tags} max_n_tags={max_n_tags} can_interact={can_interact}/>

    const handleClap = () => {
        setClapsAdded((prev) => prev + 1);
        setClapsDisplayed((prev) => prev + 1)
    }

    useEffect(() => {
        if (clapsAdded === 0) {
            return
        }

        // setConfettiVisible(true);
        setEnlargeHands(true);
        // This function submits the claps 500ms after someone has stopped clicking
        // A higher timeout, and visitors may have left the page
        // A lower timeout means more API requests
        // This is just to prevent people from spamming the API with 6 clicks / sec

        const animTimeout = setTimeout(() => {
            setEnlargeHands(false)
        }, 500);

        const submitClapTimeout = async () => {
            if (!executeRecaptcha) {
                return
            }
            const token = await executeRecaptcha('contact');
            await incrementClap(slug, token, clapsAdded);
            clapsLoaded.current += clapsAdded;
            setCookie(slug, clapsLoaded.current);
            setClapsAdded(0);
            // setConfettiVisible(false);
        }
        const timeoutId = setTimeout(submitClapTimeout, 1000);
        // Cleanup function to clear the timeout if the component unmounts
        return () => {
            clearTimeout(animTimeout);
            clearTimeout(timeoutId);
        }
    }, [clapsAdded]);


    const newHands = () => {
        // Generate a unique key whenever called to trigger re-rendering
        const key = Date.now().toString(); // Unique key based on current timestamp
        return (
            <FaHandsClapping
                key={key} // Ensure a new instance is created each time
                className={`can-clap ${enlargeHands ? 'enlarge-hands' : ''}`}
            />
        );
    };

    return (
        <StyledPostMeta className={className} {...props}>
            <div className='top-meta'>
                {max_n_tags > 1 && mainTags}
            </div>
            <div className='bottom-meta'>
                {max_n_tags < 2 && mainTags}
                {audio ?(
                    <PostAudio src={audioURL} duration={audio}/>
                ) : (
                    <FaAlignJustify/>
                )}
                <ScrollToComments
                    commentsDisplayed={commentsDisplayed}
                    can_interact={can_interact}
                />
                <ClappingHands
                    clapsDisplayed={clapsDisplayed}
                    can_interact={can_interact}
                    handleClap={handleClap}
                    clapsAdded={clapsAdded}
                    newHands={newHands}
                />
                <span><FaEye/>{viewsDisplayed}</span>
                <span dangerouslySetInnerHTML={{__html: date}} className='date'></span>
            </div>
        </StyledPostMeta>
    )
}

export default BlogMeta